import { Rows, SquareSplitHorizontal, SquareSplitVertical } from '@phosphor-icons/react';
import styles from './ViewModeToggle.module.css';

/**
 * Dónde se lee el ticket abierto. Son las tres del panel de lectura de
 * Outlook, con sus mismos nombres traducidos:
 *
 * · `right`  — lista a la izquierda, ticket a la derecha. Es la de siempre.
 * · `bottom` — lista arriba a todo el ancho (con sus columnas) y ticket debajo.
 * · `off`    — solo lista; al abrir un ticket, ocupa la pantalla entera.
 *
 * El valor se guarda en `localStorage`, así que es una preferencia POR
 * NAVEGADOR y no viaja con la cuenta. Es lo correcto para esto: depende del
 * monitor que tengas delante, no de quién eres.
 */
export type PaneMode = 'right' | 'bottom' | 'off';

export const PANE_MODES: { value: PaneMode; label: string }[] = [
  { value: 'right', label: 'Vista previa a la derecha' },
  { value: 'bottom', label: 'Vista previa debajo' },
  { value: 'off', label: 'Sin vista previa' },
];

const ICONS = {
  /* ⚠️ Los dos «SquareSplit» de Phosphor se llaman al revés de lo que uno
     espera: `Horizontal` es el cuadrado partido por una línea VERTICAL (dos
     mitades izquierda/derecha) y `Vertical` el partido por una HORIZONTAL.
     El nombre describe la dirección en la que se reparten las mitades, no la
     del corte. Comprobado en los paths de la v2.1.10 antes de elegirlos. */
  right: SquareSplitHorizontal,
  bottom: SquareSplitVertical,
  off: Rows,
} as const;

interface ViewModeToggleProps {
  value: PaneMode;
  onChange: (mode: PaneMode) => void;
}

export function ViewModeToggle({ value, onChange }: ViewModeToggleProps) {
  return (
    <div
      className={styles.group}
      role="radiogroup"
      aria-label="Dónde se abre el ticket"
    >
      {PANE_MODES.map((m) => {
        const Icon = ICONS[m.value];
        const on = value === m.value;
        return (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={on}
            /* El icono es la única etiqueta visible, así que el nombre
               accesible tiene que venir de aquí: sin `aria-label` un lector de
               pantalla anuncia tres botones vacíos. El `title` es para el
               ratón y dice lo mismo a propósito. */
            aria-label={m.label}
            title={m.label}
            className={styles.btn}
            data-on={on ? 'true' : 'false'}
            onClick={() => onChange(m.value)}
          >
            <Icon size={16} weight={on ? 'fill' : 'regular'} />
          </button>
        );
      })}
    </div>
  );
}
